import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCampaign } from '@/hooks/useCampaigns';
import { useCreatives } from '@/hooks/useCreatives';
import { deleteCreative } from '@/services/creatives';
import { formatRelativeDate } from '@/utils/date';
import type { Creative } from '@/types/api';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';
import { EditCampaignDetailsModal } from '@/components/modals/EditCampaignDetailsModal';
import { Button } from '@/components/primitives/Button';

function inferStatus(c: Creative): 'Ready' | 'Draft' {
  return c.thumbnail_url ? 'Ready' : 'Draft';
}

const STATUS_CONFIG = {
  Ready: 'bg-white text-tertiary border border-outline-variant/40',
  Draft: 'bg-surface-container-high text-on-surface-variant',
};

export default function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const id = campaignId ? parseInt(campaignId, 10) : null;
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const [isEditCampaignOpen, setIsEditCampaignOpen] = useState(false);
  const navigate = useNavigate();

  const campaign = useCampaign(id);
  const creatives = useCreatives({ campaign_id: id ?? undefined });

  async function handleDelete(e: React.MouseEvent, creativeId: number) {
    e.preventDefault();
    if (!confirm('Delete this creative?')) return;
    try {
      await deleteCreative(creativeId);
      creatives.refetch();
    } catch {
      creatives.refetch();
    }
  }

  if (campaign.loading) {
    return (
      <div className="p-8 lg:p-12 space-y-8 animate-pulse max-w-5xl">
        <div className="h-8 w-64 bg-surface-container-high rounded" />
        <div className="h-4 w-40 bg-surface-container rounded" />
        <div className="space-y-3 mt-8">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 bg-surface-container-lowest rounded-xl border border-outline-variant/10"
            />
          ))}
        </div>
      </div>
    );
  }

  if (campaign.error) {
    return (
      <div className="p-8">
        <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
          {campaign.error}
        </div>
        <Link
          to="/campaigns"
          className="mt-4 inline-flex items-center gap-2 text-primary font-bold text-sm hover:underline"
        >
          <span className="material-symbols-outlined text-lg">arrow_back</span>
          Back to Campaigns
        </Link>
      </div>
    );
  }

  const c = campaign.data;
  if (!c) return null;

  return (
    <div className="p-8 lg:p-12 max-w-5xl space-y-10">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-on-surface-variant">
        <Link to="/campaigns" className="hover:text-primary transition-colors">
          Campaigns
        </Link>
        <span className="material-symbols-outlined text-sm">chevron_right</span>
        <span className="text-on-surface font-medium">{c.name}</span>
      </div>

      {/* Campaign header */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-primary-container flex items-center justify-center">
              <span
                className="material-symbols-outlined text-primary"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                campaign
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-headline font-black tracking-tight text-on-surface">
                {c.name}
              </h1>
              <p className="text-xs text-on-surface-variant mt-0.5">
                Updated {formatRelativeDate(c.updated_at)} · Created{' '}
                {formatRelativeDate(c.created_at)}
              </p>
            </div>
          </div>
          {c.narrative && <p className="text-on-surface-variant mt-4 max-w-xl">{c.narrative}</p>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Button variant="outline" onClick={() => setIsEditCampaignOpen(true)}>
            Edit Details
          </Button>
          <button
            type="button"
            onClick={() => setIsNewCreativeOpen(true)}
            className="flex items-center gap-2 bg-primary hover:bg-primary-dim text-on-primary px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg shadow-primary/20 shrink-0 cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            New Creative
          </button>
        </div>
      </div>

      {/* Creatives in this campaign */}
      <section className="space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-outline-variant/10">
          <h2 className="text-xl font-headline font-bold text-on-surface">Creatives</h2>
          <span className="text-xs text-on-surface-variant font-medium">
            {creatives.data?.length ?? '…'} total
          </span>
        </div>

        {creatives.error && (
          <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
            {creatives.error}
          </div>
        )}

        {creatives.loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-20 bg-surface-container-lowest rounded-xl border border-outline-variant/10 animate-pulse"
              />
            ))}
          </div>
        )}

        {!creatives.loading &&
          (creatives.data ?? []).map((cr) => {
            const status = inferStatus(cr);
            return (
              <Link
                key={cr.id}
                to={`/campaigns/${id}/creatives/${cr.id}`}
                className="group flex items-center justify-between p-4 rounded-xl bg-surface-container-lowest border border-surface-variant/20 ambient-shadow hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center gap-5">
                  {/* Thumbnail */}
                  <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-surface-container-low">
                    {cr.thumbnail_url ? (
                      <img
                        src={cr.thumbnail_url}
                        alt={cr.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-container to-surface-container-low">
                        <span className="material-symbols-outlined text-primary/30 text-2xl">
                          image
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-on-surface group-hover:text-primary transition-colors">
                      {cr.name}
                    </p>
                    <p className="text-xs text-on-surface-variant mt-1">
                      {cr.template_id} · Edited {formatRelativeDate(cr.updated_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${STATUS_CONFIG[status]}`}
                  >
                    {status}
                  </span>
                  <button
                    aria-label="Delete"
                    onClick={(e) => cr.id != null && handleDelete(e, cr.id)}
                    className="flex items-center justify-center rounded-lg size-9 text-on-surface-variant hover:bg-error-container hover:text-error transition-colors cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-[20px]">delete</span>
                  </button>
                </div>
              </Link>
            );
          })}

        {!creatives.loading && creatives.data?.length === 0 && (
          <div className="py-16 text-center">
            <span className="material-symbols-outlined text-5xl text-primary/30">auto_awesome</span>
            <p className="mt-4 text-on-surface-variant">No creatives in this campaign yet.</p>
            <button
              type="button"
              onClick={() => setIsNewCreativeOpen(true)}
              className="mt-4 inline-flex items-center gap-2 bg-primary text-on-primary px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 hover:bg-primary-dim transition-all cursor-pointer"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              Create a Creative
            </button>
          </div>
        )}
      </section>

      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        campaignId={id ?? undefined}
        onCreated={(created) =>
          navigate(
            id != null ? `/campaigns/${id}/creatives/${created.id}` : `/creatives/${created.id}`,
          )
        }
      />
      <EditCampaignDetailsModal
        isOpen={isEditCampaignOpen}
        onClose={() => setIsEditCampaignOpen(false)}
        campaign={c}
        onSaved={() => {
          campaign.refetch();
          setIsEditCampaignOpen(false);
        }}
      />
    </div>
  );
}
